import { t } from "@lingui/core/macro";
import { Collection, Header, type Selection } from "react-aria-components";
import styles from "@/pages/commandcenter/Planning/SideBar/PlanningSideBar.module.scss";
import { MedomaGridListItem } from "@/components/DragAndDrop/DragAndDrop";
import { AllocatableItemTile } from "@/pages/commandcenter/Planning/AllocatableItem/AllocatableItemTile";
import type { Row } from "@tanstack/react-table";
import {
  categorySchema,
  getUniqueId,
  isEmployeePerformed,
  type IActivityOccurrenceOrGroup,
  type ITimeOfDay,
} from "@medoma/models/activities";
import {
  type IMedicalCompetence,
  medicalCompetenceDictionary,
} from "@medoma/models/shifts";
import { activityCategoryDictionary } from "@/api/Activities";
import { z } from "zod";
import { i18n } from "@lingui/core";
import {
  patientStatusDictionary,
  patientStatusSchema,
  type IArea,
  type ITeam,
} from "@medoma/models/patients";
import { getPatientNameWithStatus } from "@/api/Patients";

export type IActivityTableItem = {
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup;
  category: IActivityOccurrenceOrGroup["category"];
  patient: IActivityOccurrenceOrGroup["patient"];
  requiredCompetences: IMedicalCompetence[] | undefined;
  timespan: [Date, Date, ITimeOfDay];
  areaDisplayName: IArea["displayName"] | undefined;
  team: ITeam | undefined;
  primaryResponsible: number | undefined;
};

const groupingSchema = z.discriminatedUnion("id", [
  z.object({ id: z.literal("category"), value: categorySchema }),
  z.object({ id: z.literal("patient"), value: z.string() }),
  z.object({ id: z.literal("requiredCompetences"), value: z.string() }),
  z.object({ id: z.literal("timespan"), value: z.string() }),
  z.object({ id: z.literal("areaDisplayName"), value: z.string() }),
  z.object({ id: z.literal("team"), value: z.string() }),
  z.object({ id: z.literal("primaryResponsible"), value: z.string() }),
]);
export type IGrouping = z.infer<typeof groupingSchema>;

const requiredCompetences = (
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup,
): IMedicalCompetence[] | undefined =>
  isEmployeePerformed(activityOccurrenceOrGroup)
    ? activityOccurrenceOrGroup.requiredCompetences
    : undefined;

const typeSafeGrouping = (groupRow: Row<IActivityTableItem>) => {
  return groupingSchema.parse({
    id: groupRow.groupingColumnId,
    value: groupValue(groupRow),
  });
};
const groupHeaderDisplayValue = (grouping: IGrouping) => {
  if (grouping.id === "category") {
    return i18n._(activityCategoryDictionary[grouping.value]);
  }

  return grouping.value;
};

const groupValue = (row: Row<IActivityTableItem>) => {
  // For patients we group on id, but we display the name (which in rare cases may be duplicated)
  if (row.groupingColumnId === "patient") {
    return row.original.patient
      ? row.original.patient.status === patientStatusSchema.enum.deleted
        ? i18n._(patientStatusDictionary.deleted.singular)
        : getPatientNameWithStatus(row.original.patient, "full")
      : // If there is no patient, fallback to a hyphen
        "-";
  }

  return row.groupingValue;
};

const getTextValue = (
  activityOccurrenceOrGroup: IActivityOccurrenceOrGroup,
): string => {
  const translatedCompetences = requiredCompetences(activityOccurrenceOrGroup)
    ?.map((competence) => i18n._(medicalCompetenceDictionary[competence].short))
    .join(" ");

  const patientName = activityOccurrenceOrGroup.patient
    ? getPatientNameWithStatus(activityOccurrenceOrGroup.patient, "full")
    : t`Ingen patient`;

  const textValue = [
    i18n._(activityCategoryDictionary[activityOccurrenceOrGroup.category]),
    patientName,
    translatedCompetences,
  ];

  return textValue.filter(Boolean).join(" ");
};

export const GroupedRows = ({
  row,
  selectedKeys,
}: {
  row: Row<IActivityTableItem>;
  selectedKeys: Selection;
}) => {
  if (row.getIsGrouped()) {
    const groupRow = row;
    const grouping = typeSafeGrouping(groupRow);
    return (
      // use <Section /> instead of fragment when https://github.com/adobe/react-spectrum/issues/5230 is fixed
      <>
        <Header className={styles.header}>
          {groupRow.groupingColumnId ? (
            <>
              <span>
                {groupHeaderDisplayValue(grouping)} ({groupRow.subRows.length})
              </span>
            </>
          ) : null}
        </Header>
        <Collection items={groupRow.subRows}>
          {({ original }) => {
            const id = getUniqueId(original.activityOccurrenceOrGroup);
            return (
              <MedomaGridListItem
                textValue={getTextValue(original.activityOccurrenceOrGroup)}
                id={id}
              >
                <AllocatableItemTile
                  activityOccurrenceOrGroup={original.activityOccurrenceOrGroup}
                  isSelected={
                    selectedKeys &&
                    (selectedKeys === "all" || selectedKeys.has(id))
                  }
                />
              </MedomaGridListItem>
            );
          }}
        </Collection>
      </>
    );
  } else {
    const id = getUniqueId(row.original.activityOccurrenceOrGroup);
    return (
      <>
        <MedomaGridListItem
          textValue={getTextValue(row.original.activityOccurrenceOrGroup)}
          id={id}
        >
          <AllocatableItemTile
            activityOccurrenceOrGroup={row.original.activityOccurrenceOrGroup}
            isSelected={
              selectedKeys && (selectedKeys === "all" || selectedKeys.has(id))
            }
          />
        </MedomaGridListItem>
      </>
    );
  }
};
